// src/hooks/useOnline.ts
import { useEffect, useRef, useState } from "react";

type UseOnlineOptions = {
  pingUrl?: string;
  timeout?: number;
};

export function useOnline({
  pingUrl = "/sw.js",
  timeout = 3000,
}: UseOnlineOptions = {}) {
  const [isOnline, setIsOnline] = useState<boolean>(navigator.onLine);
  const mountedRef = useRef(true);
  const abortRef = useRef<AbortController | null>(null);

  useEffect(() => {
    mountedRef.current = true;

    const verifyOnline = async () => {
      if (!navigator.onLine) {
        setIsOnline(false);
        return;
      }

      try {
        if (abortRef.current) {
          abortRef.current.abort();
        }
        abortRef.current = new AbortController();
        const id = setTimeout(() => abortRef.current?.abort(), timeout);

        const url =
          pingUrl + (pingUrl.includes("?") ? "&" : "?") + "cb=" + Date.now();

        await fetch(url, {
          method: "GET",
          cache: "no-store",
          signal: abortRef.current.signal,
        });

        clearTimeout(id);

        if (!mountedRef.current) return;
        setIsOnline(true);
      } catch (err) {
        if (!mountedRef.current) return;
        setIsOnline(false);
      }
    };

    const handleOnline = () => verifyOnline();
    const handleOffline = () => setIsOnline(false);

    window.addEventListener("online", handleOnline);
    window.addEventListener("offline", handleOffline);

    // initial verification
    verifyOnline();

    return () => {
      mountedRef.current = false;
      window.removeEventListener("online", handleOnline);
      window.removeEventListener("offline", handleOffline);
      if (abortRef.current) abortRef.current.abort();
    };
  }, [pingUrl, timeout]);

  return isOnline;
}
